import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Intent } from '@blueprintjs/core';
import { Button, ButtonSave } from 'components/buttons';
import { FormTextInput } from 'components/form-fields';
import { Modal, ModalSize, NotificationToaster } from 'components/notifications';
import { Tooltip } from 'components/tooltip';
import { UserHasAccess } from 'components/user-access';
import { isValidEmailAddress, useValidation } from 'hooks/useValidation';
import { EmployeeReviewsService } from 'services';

const requiredAction = "EmployeeReview:Perform";
const emailSignOffMethodKey = "EMAIL";
const badRequestCode = 400;
const throttleMessage = "A sign-off email has already been sent for this review. A new one can only be sent once every 24 hours.";
const throttleDateTimeFormat = "DD/MM/YYYY HH:mm";
const sendFailureMessage = "Failed to send the sign-off email. Please try again.";
const sendSuccessMessage = "Sign-off email sent.";
const validationRules = [{ fieldName: "signOffEmailAddress", required: true, type: "email" }];

// Mirrors the [StringLength(255)] the API applies to the address on the resend request.
const emailAddressMaxLength = 255;

const defaultFunction = () => { };

// The API sends nextResendAllowedFrom only while a send is being held off, so a value here is
// the moment the window opens and can be named. Its absence is a review nothing can be resent
// for, which the generic sentence covers. The wording matches the API's own rejection text so
// the tooltip and the rejection agree.
function getThrottleMessage(nextResendAllowedFrom) {
    if (nextResendAllowedFrom == null) {
        return throttleMessage;
    }

    const nextSendTime = moment.utc(nextResendAllowedFrom).local().format(throttleDateTimeFormat);

    return `A sign-off email has already been sent for this review. Another can be sent from ${nextSendTime}.`;
}

// Resends the sign-off link for a completed review still waiting on an emailed signature.
//
// A manager-only mutation belongs outside CompletedReviewDetail, which is also rendered to the
// driver on the anonymous sign-off page with no signed-in user behind it. Whether a resend is
// allowed is the API's decision - the 24-hour window behind canResendSignOffEmail is never
// recomputed here.
export function SignOffResend(
    {
        reviewId = null,
        signOff = null,
        onSent = defaultFunction
    }
) {

    const canPerformReview = UserHasAccess(requiredAction);
    const recordedEmailAddress = signOff?.signOffEmailAddress ?? "";

    const [emailAddress, setEmailAddress] = useState(recordedEmailAddress);
    const [sending, setSending] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [, errors, validate] = useValidation();

    // The address the link was last sent to is what the field opens with: it is re-seeded
    // whenever the review is re-read, so a successful send is reflected back from the record,
    // and again on open, so an edit that was never sent is not carried into the next attempt.
    // The closed modal is left alone, so a dialog on its way out keeps showing what was typed
    // in it.
    //
    // A recorded address is judged as it is seeded, because it reaches the record from the
    // employee's profile as well as from this field, and a profile address is held to whatever
    // rule created it. Holding the send against an address with nothing said about it would
    // leave a manager no reason for it. An empty field is left unmarked until the manager
    // reaches it.
    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const seededAddress = recordedEmailAddress.trim();

        setEmailAddress(recordedEmailAddress);
        validate(seededAddress === "" ? [] : validationRules, { signOffEmailAddress: seededAddress });
    }, [recordedEmailAddress, isOpen]);

    const validationMessage = errors.signOffEmailAddress;
    const canResend = signOff?.canResendSignOffEmail === true;

    // The address is trimmed as it is sent, so it is judged trimmed: the one rule behind both
    // the send and whether the send is offered at all.
    const addressToSend = emailAddress.trim();
    const addressIsValid = addressToSend !== "" && isValidEmailAddress(addressToSend);

    // An app sign-off carries no emailed link, so only the email method has anything to resend.
    const awaitingEmailSignOff = signOff?.method === emailSignOffMethodKey &&
        signOff?.employeeSignedOffDate == null;

    // Judged on the interaction, and trimmed as it is sent, so an address is neither refused
    // for whitespace that is dropped on the way out nor marked against a manager who has not
    // reached the field yet.
    function onEmailAddressChange(event) {
        const newEmailAddress = event.target.value;

        setEmailAddress(newEmailAddress);
        validate(validationRules, { signOffEmailAddress: newEmailAddress.trim() });
    }

    function onSendClick() {
        if (!addressIsValid) {
            return;
        }

        setSending(true);

        EmployeeReviewsService.sendReviewSignOffEmail(reviewId, addressToSend).then(function () {
            NotificationToaster.show(Intent.SUCCESS, sendSuccessMessage);
            setIsOpen(false);
            // Both the recorded address and whether another send is allowed are derived by the
            // API, so the record is re-read rather than guessed at from here. The re-read is
            // returned so the button holds disabled until the new state is in. Where that read
            // fails the record stands as it was and the button comes back enabled, leaving a
            // retry against a window only the server rules on.
            return onSent();
        }, function (error) {
            if (error?.status === badRequestCode) {
                NotificationToaster.show(Intent.WARNING, error.data);
            } else {
                NotificationToaster.show(Intent.DANGER, sendFailureMessage);
            }
        }).finally(function () {
            setSending(false);
        });
    }

    if (!awaitingEmailSignOff || !canPerformReview) {
        return null;
    }

    const sendButton = (
        <Button
            id="sign-off-resend-email"
            text="Resend Sign-Off Email"
            icon="envelope"
            intent="primary"
            disabled={!canResend || sending}
            onClick={() => setIsOpen(true)}
        />
    );

    let sendControl;
    if (canResend) {
        sendControl = sendButton;
    } else {
        // A native disabled button suppresses hover events, so wrap it in a span to give the
        // tooltip a hoverable target (Blueprint's documented approach).
        sendControl = (
            <Tooltip content={getThrottleMessage(signOff?.nextResendAllowedFrom)}><span>{sendButton}</span></Tooltip>
        );
    }

    return (
        <Fragment>
            {sendControl}
            <Modal
                title="Resend Sign-Off Email"
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                size={ModalSize.SMALL}
                updateStateOnClose={false}
                clickOutsideClose={false}
                footer={(
                    <div className="bp6-dialog-footer-actions">
                        <Button
                            id="sign-off-resend-email-cancel"
                            text="Cancel"
                            intent="secondary"
                            onClick={() => setIsOpen(false)}
                            disabled={sending}
                        />
                        {/* ButtonSave shows its spinner for disabled and not for simpleDisabled,
                            so the in-flight send is the only thing that may use disabled. An
                            address that is not yet sendable holds the button on simpleDisabled,
                            which greys it without reporting a send that is not happening. */}
                        <ButtonSave
                            id="sign-off-resend-email-send"
                            text="Send"
                            icon="envelope"
                            onClick={onSendClick}
                            simpleDisabled={!addressIsValid}
                            disabled={sending}
                        />
                    </div>
                )}
            >
                <FormTextInput
                    id="sign-off-resend-email-address"
                    headingText="Driver Email Address"
                    helperText="The sign-off link is sent to this address for this review only - the driver's profile is not changed."
                    value={emailAddress}
                    maxLength={emailAddressMaxLength}
                    disabled={sending}
                    dangerHelperText={validationMessage}
                    onChange={onEmailAddressChange}
                />
            </Modal>
        </Fragment>
    );
}

SignOffResend.propTypes = {
    reviewId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    signOff: PropTypes.object,
    onSent: PropTypes.func
};
